/**
 * Teak Quote Request — form controller on /pages/quote-request.
 *
 * - Renders the items from localStorage in the summary aside
 * - On submit, POSTs to the Cloudflare Worker endpoint
 * - Handles loading state, error display, and success redirect
 */

(function () {
  if (window.__TeakQuoteRequestInit) return;
  window.__TeakQuoteRequestInit = true;

  function formatMoney(cents, currency) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency || 'AUD',
    }).format(Number((cents / 100).toFixed(2))).replace(/\.00\b/g, '');
  }

  function init() {
    const root = document.querySelector('[data-teak-quote-request]');
    if (!root) return;
    if (!window.TeakQuote) {
      console.error('TeakQuote storage missing on quote request page');
      return;
    }

    const config = window.TeakQuoteConfig || {};
    const endpoint = config.endpoint;
    const confirmedUrl = config.quoteConfirmedUrl || '/pages/quote-confirmed';
    const quoteListUrl = config.quoteListUrl || '/pages/quote';

    if (!endpoint) {
      console.error('TeakQuoteConfig.endpoint is not set — cannot submit quote');
    }

    const layout = root.querySelector('[data-teak-qr-layout]');
    const emptyState = root.querySelector('[data-teak-qr-empty]');
    const form = root.querySelector('[data-teak-qr-form]');
    const submitBtn = root.querySelector('[data-teak-qr-submit]');
    const submitLabel = root.querySelector('[data-teak-qr-submit-label]');
    const errorBox = root.querySelector('[data-teak-qr-error]');
    const summaryItemsEl = root.querySelector('[data-teak-qr-summary-items]');
    const summaryCount = root.querySelector('[data-teak-qr-summary-count]');
    const summaryTotal = root.querySelector('[data-teak-qr-summary-total]');
    const itemTemplate = root.querySelector('[data-teak-qr-summary-item-template]');

    function renderSummary() {
      const items = window.TeakQuote.all();

      if (items.length === 0) {
        emptyState.hidden = false;
        layout.hidden = true;
        return false;
      }

      emptyState.hidden = true;
      layout.hidden = false;

      summaryItemsEl.innerHTML = '';
      items.forEach((item) => {
        const node = itemTemplate.content.firstElementChild.cloneNode(true);
        node.querySelector('[data-img]').src = item.imageUrl;
        node.querySelector('[data-img]').alt = item.title;
        node.querySelector('[data-title]').textContent = item.title;
        const variantEl = node.querySelector('[data-variant]');
        if (item.variantTitle) {
          variantEl.textContent = item.variantTitle;
          variantEl.hidden = false;
        }
        node.querySelector('[data-qty]').textContent = item.quantity;
        node.querySelector('[data-price]').textContent = formatMoney(item.priceCents, item.currency);
        summaryItemsEl.appendChild(node);
      });

      const totalCents = items.reduce((sum, item) => sum + (item.priceCents * item.quantity), 0);
      const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
      summaryCount.textContent = totalQty;
      summaryTotal.textContent = formatMoney(totalCents, items[0]?.currency || 'AUD');
      return true;
    }

    function clearError() {
      errorBox.hidden = true;
      errorBox.textContent = '';
      form.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    }

    function showError(message) {
      errorBox.textContent = message;
      errorBox.hidden = false;
      errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function setSubmitting(isSubmitting) {
      submitBtn.disabled = isSubmitting;
      submitLabel.textContent = isSubmitting ? 'Sending…' : 'Send quote request';
    }

    function collectFormPayload() {
      const data = new FormData(form);
      const items = window.TeakQuote.all().map((item) => ({
        variant_id: item.variantId,
        quantity: item.quantity,
      }));

      return {
        customer: {
          first_name: (data.get('first_name') || '').trim(),
          last_name: (data.get('last_name') || '').trim(),
          email: (data.get('email') || '').trim(),
          phone: (data.get('phone') || '').trim(),
        },
        shipping_address: {
          address1: (data.get('address1') || '').trim(),
          address2: (data.get('address2') || '').trim(),
          city: (data.get('city') || '').trim(),
          province: (data.get('province') || '').trim(),
          zip: (data.get('zip') || '').trim(),
          country: (data.get('country') || 'Australia').trim(),
        },
        items,
        delivery_notes: (data.get('delivery_notes') || '').trim(),
        customer_notes: (data.get('customer_notes') || '').trim(),
      };
    }

    // Australian postcode ranges per state. Used to flag obvious
    // mismatches between selected state and entered postcode.
    // Sources cross-checked with Australia Post + ABS reference.
    const AU_POSTCODE_RANGES = {
      'Australian Capital Territory': [[200, 299], [2600, 2618], [2900, 2920]],
      'New South Wales': [[1000, 1999], [2000, 2599], [2619, 2899], [2921, 2999]],
      'Northern Territory': [[800, 999]],
      'Queensland': [[4000, 4999], [9000, 9999]],
      'South Australia': [[5000, 5999]],
      'Tasmania': [[7000, 7999]],
      'Victoria': [[3000, 3999], [8000, 8999]],
      'Western Australia': [[6000, 6999]],
    };

    function postcodeMatchesState(zip, province) {
      const ranges = AU_POSTCODE_RANGES[province];
      if (!ranges) return true; // unknown state — skip check
      const numeric = parseInt(zip, 10);
      if (!Number.isFinite(numeric)) return false;
      return ranges.some(([min, max]) => numeric >= min && numeric <= max);
    }

    // Field-level validators. Each returns an error string (or null
    // for pass). Keys map to the form input names.
    const VALIDATORS = {
      first_name: (v) => (v ? null : 'Required'),
      last_name: (v) => (v ? null : 'Required'),
      email: (v) => {
        if (!v) return 'Required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email';
        return null;
      },
      phone: (v) => {
        if (!v) return 'Required';
        const digits = v.replace(/\D/g, '');
        if (digits.length < 8) return 'Enter a valid phone number';
        return null;
      },
      address1: (v) => (v ? null : 'Required'),
      city: (v) => (v ? null : 'Required'),
      zip: (v, all) => {
        if (!v) return 'Required';
        if (!/^\d{4}$/.test(v)) return 'Postcode must be 4 digits';
        if (all.province && !postcodeMatchesState(v, all.province)) {
          return 'Postcode does not match the selected state';
        }
        return null;
      },
      province: (v, all) => {
        if (v && all.zip && /^\d{4}$/.test(all.zip) && !postcodeMatchesState(all.zip, v)) {
          return 'State does not match the entered postcode';
        }
        return null;
      },
    };

    function collectFlatFields() {
      const data = new FormData(form);
      return {
        first_name: (data.get('first_name') || '').trim(),
        last_name: (data.get('last_name') || '').trim(),
        email: (data.get('email') || '').trim(),
        phone: (data.get('phone') || '').trim(),
        address1: (data.get('address1') || '').trim(),
        address2: (data.get('address2') || '').trim(),
        city: (data.get('city') || '').trim(),
        province: (data.get('province') || '').trim(),
        zip: (data.get('zip') || '').trim(),
      };
    }

    function showFieldError(name, message) {
      const input = form.querySelector(`[name="${name}"]`);
      if (!input) return;
      input.setAttribute('aria-invalid', 'true');

      const fieldEl = input.closest('.teak-qr__field');
      if (!fieldEl) return;

      let errorEl = fieldEl.querySelector('.teak-qr__field-error');
      if (!errorEl) {
        errorEl = document.createElement('span');
        errorEl.className = 'teak-qr__field-error';
        fieldEl.appendChild(errorEl);
      }
      errorEl.textContent = message;
    }

    function clearFieldError(name) {
      const input = form.querySelector(`[name="${name}"]`);
      if (!input) return;
      input.removeAttribute('aria-invalid');
      const fieldEl = input.closest('.teak-qr__field');
      const errorEl = fieldEl?.querySelector('.teak-qr__field-error');
      if (errorEl) errorEl.remove();
    }

    function validateAll() {
      const all = collectFlatFields();
      const errors = {};
      Object.keys(VALIDATORS).forEach((name) => {
        const err = VALIDATORS[name](all[name], all);
        if (err) errors[name] = err;
        else clearFieldError(name);
      });
      Object.entries(errors).forEach(([name, msg]) => showFieldError(name, msg));
      return errors;
    }

    function clientValidate(payload) {
      const fieldErrors = validateAll();
      const missing = Object.keys(fieldErrors);
      if (payload.items.length === 0) missing.push('items');
      return missing;
    }

    async function submitForm(event) {
      event.preventDefault();
      clearError();

      if (!endpoint) {
        showError('Quote submission is not configured. Please email sales@teakplace.com.au directly.');
        return;
      }

      const payload = collectFormPayload();
      const issues = clientValidate(payload);
      if (issues.length > 0) {
        showError('Please fill in all required fields marked with *.');
        return;
      }

      setSubmitting(true);
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok) {
          // Map server-side field errors back to inputs where possible.
          // Worker returns details: [{ field: "shipping_address.zip", message: "..." }].
          const details = Array.isArray(result?.details) ? result.details : [];
          const serverFieldMap = {
            'customer.first_name': 'first_name',
            'customer.last_name': 'last_name',
            'customer.email': 'email',
            'customer.phone': 'phone',
            'shipping_address.address1': 'address1',
            'shipping_address.city': 'city',
            'shipping_address.province': 'province',
            'shipping_address.zip': 'zip',
            'shipping_address.country': 'country',
          };
          let mapped = 0;
          details.forEach((d) => {
            const formField = serverFieldMap[d?.field];
            if (formField && d.message) {
              showFieldError(formField, d.message);
              mapped += 1;
            }
          });
          if (mapped > 0) {
            showError('Please correct the highlighted fields and try again.');
          } else {
            // Never surface a raw machine code (e.g. "shopify_request_failed")
            // to the customer. Show any human-readable detail if present,
            // otherwise a friendly fallback — and log the technical code for us.
            if (result?.error) {
              console.error('Quote submission failed:', result.error, result.message || '');
            }
            const message = details[0]?.message || 'We could not submit your quote right now. Please try again, or email sales@teakplace.com.au and we will sort it out.';
            showError(message);
          }
          setSubmitting(false);
          return;
        }

        // Persist the draft order info for the confirmation page,
        // then clear the local quote list and redirect.
        try {
          sessionStorage.setItem('teak_last_quote', JSON.stringify({
            draft: result.draft_order,
            customer: payload.customer,
            submittedAt: Date.now(),
          }));
        } catch (err) {
          // Non-fatal: confirmation page will just show generic copy.
        }

        window.TeakQuote.clear();
        window.location.href = confirmedUrl;
      } catch (err) {
        showError('Could not reach the quote service. Check your internet connection and try again.');
        setSubmitting(false);
      }
    }

    function bindLiveValidation() {
      // Validate a field on blur (or change for selects), and re-check
      // the postcode/province pair together so users see the error
      // disappear as soon as they fix either side.
      Object.keys(VALIDATORS).forEach((name) => {
        const input = form.querySelector(`[name="${name}"]`);
        if (!input) return;
        const eventName = input.tagName === 'SELECT' ? 'change' : 'blur';
        input.addEventListener(eventName, () => {
          const all = collectFlatFields();
          const err = VALIDATORS[name](all[name], all);
          if (err) showFieldError(name, err);
          else clearFieldError(name);

          // Postcode <-> province are linked; re-check the partner.
          if (name === 'zip' || name === 'province') {
            const other = name === 'zip' ? 'province' : 'zip';
            const otherErr = VALIDATORS[other](all[other], all);
            if (otherErr) showFieldError(other, otherErr);
            else clearFieldError(other);
          }
        });

        // Clear an existing error as soon as the user starts typing
        // again so the form does not feel punishing.
        input.addEventListener('input', () => {
          if (input.getAttribute('aria-invalid') === 'true') {
            clearFieldError(name);
          }
        });
      });
    }

    const hasItems = renderSummary();
    if (hasItems) {
      form.addEventListener('submit', submitForm);
      bindLiveValidation();
    }

    document.addEventListener('teak:quote:changed', () => {
      // If items change while the customer is still on this page (e.g.
      // they remove the last item via another tab), re-render.
      renderSummary();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
